import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { getUserPreferences } from '../../utils/userPreferences';

/**
 * Map default view preferences to actual route paths
 */
const VIEW_TO_ROUTE_MAP = {
    'home': '/',
    'dashboard': '/dashboard',
    'samples': '/samples',
    'analysis': '/lab',
    'qa': '/qa',
    'clients': '/clients',
    'configurations': '/configurations'
};

/**
 * DefaultViewRedirect component handles redirecting authenticated users
 * to their preferred default view when they visit the home page
 */
const DefaultViewRedirect = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isAuthenticated = useIsAuthenticated();
    const { accounts } = useMsal();
    const hasCheckedRedirect = useRef(false);
    const [isReady, setIsReady] = useState(false);

    // Wait a bit for MSAL to initialize
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsReady(true);
        }, 300);
        
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        // Only redirect once, when ready, authenticated, and on home page
        if (isReady && !hasCheckedRedirect.current && isAuthenticated && location.pathname === '/') {
            hasCheckedRedirect.current = true;
            
            const user = accounts.length > 0 ? accounts[0] : null;
            const username = user?.username;

            if (username) {
                const preferences = getUserPreferences(username);
                const defaultView = preferences.defaultView || 'home';
                
                // Only redirect if the default view is not 'home'
                if (defaultView !== 'home') {
                    const targetRoute = VIEW_TO_ROUTE_MAP[defaultView];
                    
                    if (targetRoute && targetRoute !== '/') {
                        // Use replace to avoid adding to browser history
                        navigate(targetRoute, { replace: true });
                    }
                }
            }
        }
    }, [isReady, isAuthenticated, location.pathname, accounts, navigate]);

    return children;
};

export default DefaultViewRedirect;
